import type { StoreActionApi } from 'react-sweet-state';
import { createStore, createHook } from 'react-sweet-state';

type ContentId = string;

interface ContentTreeSelectionState {
	// Count represents the number of selected content items and its descendants (if applicable).
	count: number;
	isMultiSelectActive: boolean;
	selectedContent: Record<ContentId, { selectedDescendantCount: number }>;
}

const initialState: ContentTreeSelectionState = {
	isMultiSelectActive: false,
	selectedContent: {},
	count: 0,
};

const actions = {
	enterMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				isMultiSelectActive: true,
				selectedContent: {},
				count: 0,
			});
		},
	exitMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				isMultiSelectActive: false,
				selectedContent: {},
				count: 0,
			});
		},
	selectContent:
		(contentId: ContentId) =>
		({ setState, getState }) => {
			const { selectedContent } = getState();
			const newSelectedContent = {
				...selectedContent,
				[contentId]: {
					selectedDescendantCount: 0, // value from archivableDescendantsCount
				},
			};

			setState({
				selectedContent: newSelectedContent,
				count: getState().count + 1, // Increment parent and nested children count
			});
		},
	unselectContent:
		(contentId: ContentId) =>
		({ setState, getState }) => {
			const { selectedContent } = getState();

			const { [contentId]: _, ...rest } = selectedContent;

			setState({
				selectedContent: rest,
				count: getState().count - 1, // Decrement parent and nested children count
			});
		},
	clearSelection:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				selectedContent: {},
				count: 0,
			});
		},
};

const Store = createStore<ContentTreeSelectionState, typeof actions>({
	initialState,
	actions,
	name: 'contentTreeSelection',
});

export const useContentTreeSelection = createHook(Store);
